import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ChevronDown, ChevronUp, HelpCircle, MessageCircle, Search } from "lucide-react";

const FAQ_CATEGORIES = [
  {
    title: "Getting Started",
    icon: "🎮",
    faqs: [
      {
        q: "How do I create an account?",
        a: "Tap 'Register' on the homepage, enter your email and choose a password. You'll pick a player name and you're in! Your progress syncs automatically so you can play on any device."
      },
      {
        q: "How do I play Fruit Crush?",
        a: "Swap two adjacent fruits to make a line of 3 or more matching fruits. They'll pop, you'll score points, and new fruits fall from above. Each level has a goal — hit the target score, collect specific fruits, or defeat a boss — all within a limited number of moves."
      },
      {
        q: "What are Worlds?",
        a: "The game is divided into 21 themed Worlds, each with 10 levels. You start in the Enchanted Forest and journey through places like Ocean Depths, Frozen Peaks, Volcanic Core, and eventually the Cosmic Nebula. Each World has its own look, music, and special mechanics."
      },
      {
        q: "Can I have multiple profiles?",
        a: "Yes! You can create up to 3 profiles on one account. Each profile has its own progress, inventory, and stats — perfect if siblings or friends want their own save."
      },
      {
        q: "How do I switch between profiles?",
        a: "Open the hamburger menu (top-left on the level map) and tap 'Switch'. You'll see all your profiles — just tap the one you want to play. Each profile's progress is completely independent."
      },
      {
        q: "What's the best way to learn the game?",
        a: "Play through the tutorial when you first start — it walks you through matching, combos, and power-ups in 7 quick steps. You can replay it anytime from the Help option in the hamburger menu."
      },
      {
        q: "Is Fruit Crush free to play?",
        a: "Yes! The entire game is free to play. You can progress through all 200+ levels without spending anything. Optional purchases like the VIP membership and star packs enhance the experience but aren't required."
      }
    ]
  },
  {
    title: "Gameplay & Mechanics",
    icon: "🍓",
    faqs: [
      {
        q: "How do I create special power-ups?",
        a: "Match 4 in a row for a Striped Fruit (clears a whole row or column). Match in an L or T shape for a Wrapped Bomb (explodes in a 3x3 area). Match 5 in a row for the legendary Disco Ball — it clears every fruit of the color you swap it with!"
      },
      {
        q: "What happens when I combine two special fruits?",
        a: "Swapping two specials together creates devastation! Striped + Striped clears a row AND column. Striped + Wrapped creates a giant cross blast. Wrapped + Wrapped is a massive 5x5 explosion. And Disco Ball + anything? Pure chaos — in the best way."
      },
      {
        q: "How does the life system work?",
        a: "You have a maximum of 5 lives (more with VIP). Failing a level costs 1 life. Lives regenerate every 30 minutes, or you can buy refills in the Shop. VIP members get faster regen and more max lives."
      },
      {
        q: "What are Boss Battles?",
        a: "Every World has a boss at the end. Bosses have health bars and attack patterns — make matches to deal damage while dodging their special moves. Some bosses freeze tiles, others add blockers, and the toughest ones change the entire board. Beat them to unlock the next World!"
      },
      {
        q: "What is the Lava mechanic?",
        a: "In certain levels (especially in Volcanic Core), lava rises from the bottom of the board over time. Any fruit touched by lava is destroyed — and if lava reaches the top, you lose! Work fast and focus on matches near the lava line."
      },
      {
        q: "How do cascade bonuses work?",
        a: "When fruits fall after a match and create new matches automatically, that's a cascade! Each cascade in a chain gives a bigger score multiplier. With the right equipment (like the Thunder Axe), cascades become even more powerful."
      },
      {
        q: "What are the different level objectives?",
        a: "Levels can have various goals: reach a target score, collect a certain number of specific fruits, break all ice/stone blockers, defeat a boss, or survive a set number of turns. Some levels combine multiple objectives!"
      },
      {
        q: "How do I earn stars on a level?",
        a: "Each level awards 1-3 stars based on your score. 1 star = completed the objective. 2 stars = exceeded the target. 3 stars = crushed it with an outstanding score. Stars contribute to your World Mastery progress."
      },
      {
        q: "What happens when I run out of moves?",
        a: "If you run out of moves before completing the objective, you fail the level and lose 1 life. You'll see an 'Almost There' indicator showing how close you were. You can retry immediately if you have lives remaining."
      },
      {
        q: "Can I replay levels I've already completed?",
        a: "Absolutely! Replaying levels is a great way to improve your star rating, earn more coins, and farm item drops. Some crafting materials only drop from specific World ranges, so replaying is essential for collectors."
      },
      {
        q: "What are environmental effects?",
        a: "When you hit high combos (5x+), the environment reacts based on the current World theme. In Frozen Peaks you'll see snowflakes swirl, in Volcanic Core embers fly, and in Storm Kingdom lightning flashes. These are visual celebrations of your skill!"
      }
    ]
  },
  {
    title: "Worlds & Levels",
    icon: "🌍",
    faqs: [
      {
        q: "How many Worlds are there?",
        a: "There are 21 Worlds, each with 10 levels, totaling 210 levels. Worlds include Enchanted Forest, Ocean Depths, Frozen Peaks, Volcanic Core, Mushroom Cavern, Dark Forest, Storm Kingdom, Ancient Ruins, Crystal Caves, Moonlight Garden, Desert Oasis, Candy Kingdom, Haunted Manor, Sky Islands, Jungle Temple, Pirate Cove, Neon City, Dragon's Lair, Cosmic Nebula, Shadow Realm, and Celestial Palace."
      },
      {
        q: "What makes each World unique?",
        a: "Every World has unique visual themes, background music, special mechanics, and exclusive item drops. For example, Ocean Depths has underwater currents that shift fruits, Frozen Peaks has ice tiles that need double-matching to break, and Volcanic Core has the rising lava mechanic."
      },
      {
        q: "What is World Lore?",
        a: "When you enter a new World for the first time, you'll see a lore popup that tells the story of that realm. It adds context to your journey — why are the fruits enchanted in the Moonlight Garden? What ancient secrets lie in the Crystal Caves? Each World has its own narrative."
      },
      {
        q: "How do I unlock new Worlds?",
        a: "Complete the boss level (level 10) of the current World to unlock the next one. You don't need to 3-star every level — just beat the boss! However, going back for 3 stars earns you the World Mastery Badge."
      },
      {
        q: "What are World Mastery Badges?",
        a: "3-star every single level in a World and you'll earn its Mastery Badge — a prestigious icon displayed on your profile for everyone to see. It's the ultimate proof you've conquered that World! There are 21 badges to collect."
      },
      {
        q: "Do different Worlds drop different items?",
        a: "Yes! Each World has exclusive loot drops. Ocean Depths drops Coral Charms and Deep Sea Compasses, Frozen Peaks drops Yeti Fur and Snow Globes, Ancient Ruins drops Pharaoh Scarabs and Eye of Ra. Check the Codex to see which items come from which World."
      },
      {
        q: "Why does the same level feel different sometimes?",
        a: "Board layouts have some randomness — the starting fruit positions change each attempt. This means you need to adapt your strategy each time rather than memorizing a solution. It keeps every attempt fresh!"
      }
    ]
  },
  {
    title: "Items & Equipment",
    icon: "⚔️",
    faqs: [
      {
        q: "What types of items are there?",
        a: "There are 200 items across 9 categories: Weapons (15 — boost match scores), Armor (15 — survival bonuses), Accessories (20 — XP/coin/utility bonuses), Consumables (50 — one-time use), Gems (20), World Loot (21), Crafting Materials (20), Trophies (20), and Seasonal items (19)."
      },
      {
        q: "How do I get items?",
        a: "Items drop randomly when you complete levels. Higher Worlds drop rarer items. You can also craft equipment using materials, buy consumables in the Shop, or earn exclusive items from events, achievements, and the Battle Pass."
      },
      {
        q: "What are item rarities?",
        a: "Items come in 5 rarities: Common (gray), Uncommon (green), Rare (blue), Epic (purple), and Legendary (gold). Rarer items have stronger effects and lower drop rates. Legendary items often have unique abilities you won't find anywhere else — like Excalibur which heals moves every 5 turns."
      },
      {
        q: "How do I equip items?",
        a: "Go to your Inventory and tap any equippable item (weapons, armor, or accessories). You have one slot for each type. Equipped items boost your stats during gameplay — your weapon increases match scores, armor helps with survival, and accessories give utility bonuses."
      },
      {
        q: "Can I equip multiple weapons at once?",
        a: "No — you have one slot per type: one weapon, one armor, one accessory. Choose your loadout based on the level you're facing. Going into a fire World? Equip fire-resistant armor. Tackling a boss? Use your highest-damage weapon."
      },
      {
        q: "What is the Codex?",
        a: "The Codex is your collection book showing all 200 items in the game. Undiscovered items appear as silhouettes. Filter by category, rarity, or search by name. Tap any item to see its full stats, effects, and how to obtain it. Can you discover them all?"
      },
      {
        q: "What are Consumables and how do I use them?",
        a: "Consumables are one-time-use items activated during a level. Extra Moves gives you +5 moves, Fruit Magnet pulls matching fruits together, Crystal Ball reveals the best move, and Guardian Shield protects against boss attacks for 3 turns. Use them wisely on tough levels!"
      },
      {
        q: "What are Gems used for?",
        a: "Gems are collectible items with trade value. Diamond Hearts, Sapphire Orbs, Emerald Fragments, and others can be sold for coins or traded in the Grand Exchange. Some are quite rare and sought after by other players."
      },
      {
        q: "What are Trophies?",
        a: "Trophies are achievement rewards — you earn them by hitting milestones like completing all levels in a World, reaching combo streaks, or winning PvP matches. They're displayed on your profile and prove your accomplishments."
      },
      {
        q: "What are Seasonal items?",
        a: "Seasonal items are only available during special events throughout the year — think Halloween, Winter Holidays, Spring Festival, etc. They're cosmetic collectibles that show you were there when the event happened. Miss one and you'll have to wait until it returns!"
      }
    ]
  },
  {
    title: "Crafting & The Forge",
    icon: "🔨",
    faqs: [
      {
        q: "How does Crafting work?",
        a: "Open the Forge from the bottom nav bar or hamburger menu. Browse recipes for weapons, armor, and accessories. Each recipe requires specific crafting materials plus coins. When you have everything, hit 'Forge' and the item is added to your inventory!"
      },
      {
        q: "How many recipes are there?",
        a: "There are 31 crafting recipes: 11 weapons, 11 armor pieces, and 12 accessories. They span all 5 rarity tiers — from simple Common items like the Wooden Sword to Legendary masterpieces like the Infinity Gauntlet."
      },
      {
        q: "How do I unlock higher-tier recipes?",
        a: "Recipes unlock based on your highest completed level. Common recipes are available from the start. Uncommon unlock around level 30, Rare at 60-80, Epic at 90-120, and Legendary recipes require reaching level 180+. Keep progressing to unlock them all!"
      },
      {
        q: "Where do I find crafting materials?",
        a: "Crafting materials drop from level completions. Basic materials like Iron Ore and Wood Planks drop in early Worlds. Rare materials like Dragon Hide and Volcanic Glass drop in later Worlds. The rarest materials — Essence of Creation and Celestial Thread — only drop from the final Worlds."
      },
      {
        q: "Can I craft the same item multiple times?",
        a: "Yes! Each forge creates one copy of the item. The recipe tracks how many times you've crafted it. This is useful for stocking up on items you might want to trade in the Grand Exchange."
      },
      {
        q: "What's the most expensive recipe?",
        a: "The Infinity Gauntlet — a Legendary accessory requiring 3 Essence of Creation, 3 Celestial Thread, 3 Void Essence, 3 Adamantine Ore, and 30,000 coins. It's the ultimate crafting challenge but grants incredible bonuses."
      },
      {
        q: "I have the materials but the Forge button is grayed out. Why?",
        a: "Check two things: 1) Do you have enough coins? Each recipe has a coin cost shown below the materials. 2) Have you reached the required level? Some recipes need you to progress further in the game before they unlock."
      },
      {
        q: "What's the best item to craft first?",
        a: "Start with a Wooden Sword (3 Wood Planks + 2 Leather Strips + 200 coins) — it's cheap and gives an immediate match score boost. Then work toward the Iron Blade once you reach World 3 for a meaningful upgrade."
      }
    ]
  },
  {
    title: "Social & PvP",
    icon: "🤝",
    faqs: [
      {
        q: "How do I add friends?",
        a: "Go to the Social page from the hamburger menu. Search for players by their name and send a friend request. Once they accept, you'll see each other's progress and can compare scores."
      },
      {
        q: "What is Ghost Score?",
        a: "When you play a level, you'll see your best friend's score displayed as a 'ghost' target to beat. It's a friendly asynchronous competition — no need to be online at the same time."
      },
      {
        q: "How does PvP work?",
        a: "Challenge friends or get matched with random opponents. You both play the same level simultaneously — whoever gets the higher score wins! PvP earns you ranking points and exclusive rewards."
      },
      {
        q: "What is the Grand Exchange?",
        a: "The Grand Exchange is the player marketplace. List items for sale at your chosen price, or browse and buy from other players. It's driven by supply and demand — rare items can fetch high prices!"
      },
      {
        q: "Can I trade items directly with friends?",
        a: "Currently, trading happens through the Grand Exchange marketplace. Direct friend-to-friend trades are on our roadmap for a future update!"
      },
      {
        q: "How do I see my friends' progress?",
        a: "Visit the Social page to see your friends list. Each friend's card shows their current level, World, and recent activity. You can also see their Ghost Scores on any level you play."
      }
    ]
  },
  {
    title: "Progression & Rewards",
    icon: "⭐",
    faqs: [
      {
        q: "How does XP and leveling work?",
        a: "You earn XP from completing levels, winning PvP matches, and finishing daily challenges. XP fills your level bar — each level up earns coins and sometimes items. Your player level is separate from the game levels you've completed."
      },
      {
        q: "What are Daily Challenges?",
        a: "Every day you get fresh challenges like 'Complete 5 levels', 'Make 10 cascade matches', or 'Use 3 power-ups'. Completing challenges rewards coins, XP, and sometimes rare items. The harder the challenge, the bigger the reward."
      },
      {
        q: "How do Daily Rewards work?",
        a: "Log in each day to claim your Daily Reward. Day 1 gives 50 coins, and the amount increases each day up to Day 7 (300 coins + a bonus item). Maintain your streak for maximum rewards — missing a day resets your streak!"
      },
      {
        q: "What is the Battle Pass?",
        a: "The Battle Pass is a seasonal reward track. There's a free track with basic rewards (coins, common items), and a premium track with exclusive cosmetics, rare items, and bonus resources. The premium track unlocks with VIP membership."
      },
      {
        q: "What are Coins and Stars used for?",
        a: "Coins are the main currency earned from playing — use them for crafting, buying consumables, and trading. Stars are the premium currency for purchasing boosters, cosmetics, and VIP membership."
      },
      {
        q: "How do I earn more Coins?",
        a: "Complete levels (more stars = more coins), finish daily challenges, claim daily rewards, sell items in the Grand Exchange, and win PvP matches. VIP members also get a permanent coin multiplier bonus."
      },
      {
        q: "What are Limited-Time Events?",
        a: "Events like Friday Night Frenzy (double coins), Weekend Double XP, and Midweek Madness appear as banners on your level map. They offer temporary boosts and sometimes exclusive rewards. Check back regularly — you don't want to miss them!"
      },
      {
        q: "Is there a way to earn Stars without buying them?",
        a: "Stars can be earned through the Battle Pass free track, special events, and achievement milestones. However, the primary way to get Stars is through the in-game Shop."
      }
    ]
  },
  {
    title: "VIP & Membership",
    icon: "👑",
    faqs: [
      {
        q: "What does VIP membership include?",
        a: "VIP members get: Premium Battle Pass access, extra daily lives (8 instead of 5), faster life regeneration (20 min instead of 30), exclusive cosmetic items, a permanent coin multiplier, priority PvP matchmaking, and a shiny crown badge on their profile."
      },
      {
        q: "How do I become a VIP member?",
        a: "Visit the Shop (accessible from the level map) and select the VIP Membership option. You can choose monthly or annual plans. Payment is securely processed through Stripe."
      },
      {
        q: "Can I cancel my membership?",
        a: "Yes, cancel anytime from your Profile page. You'll keep VIP benefits until the end of your current billing period. Any items or cosmetics earned while VIP are yours forever."
      },
      {
        q: "What's the difference between free and VIP players?",
        a: "Free players have full access to all 200+ levels and core gameplay. VIP adds quality-of-life perks: more lives, faster regen, the premium Battle Pass track, exclusive cosmetics, and a coin multiplier. It's about convenience and customization, not a pay-to-win advantage."
      },
      {
        q: "Do VIP benefits apply to all my profiles?",
        a: "Yes! VIP membership is account-wide. All profiles under your account benefit from VIP perks — extra lives, faster regen, premium Battle Pass, and the coin bonus."
      }
    ]
  },
  {
    title: "Advanced Strategies",
    icon: "🧠",
    faqs: [
      {
        q: "What's the best strategy for high scores?",
        a: "Focus on creating cascade chains — matches at the bottom of the board create more cascades as fruits fall. Combine special fruits for massive point multipliers. Equip your best weapon for match score bonuses, and save Disco Balls for combining with other specials."
      },
      {
        q: "How do I beat tough boss levels?",
        a: "Equip your strongest weapon and fire-resistant armor. Focus on making matches near the boss's weak points. Save your special fruit combos for big damage bursts. Use consumables like Guardian Shield when the boss is about to attack, and Crystal Ball to find optimal moves."
      },
      {
        q: "What's the best equipment loadout?",
        a: "It depends on the World! For general play: Excalibur (weapon), Crystal Armor (armor), Crown of Wisdom (accessory) is a strong combo. For boss fights: Dragon Lance + Dragon Scale Mail for that boss damage bonus. For farming coins: equip anything with coin multiplier effects."
      },
      {
        q: "How do I maximize item drops?",
        a: "Higher-star completions have better drop rates. Later Worlds drop rarer items. Replaying levels still gives drops, so farm Worlds that drop the materials you need. Double XP events also boost drop rates on some items."
      },
      {
        q: "Any tips for the Lava levels?",
        a: "Work from the bottom up — clearing fruits near the lava line slows its rise. Prioritize horizontal matches at the bottom row. Striped Fruits that clear rows are especially valuable. And equip fire-resistant armor if you have it!"
      },
      {
        q: "How do I get 3 stars on every level?",
        a: "Focus on creating cascades and special fruit combos rather than simple 3-matches. Plan 2-3 moves ahead. Use the Crystal Ball consumable to find the optimal move when stuck. Don't waste moves on matches that don't contribute to your objective."
      },
      {
        q: "What's the fastest way to level up?",
        a: "Complete daily challenges for XP bonuses. Play during Double XP events. Equip accessories with XP boost effects. And focus on 3-starring levels you haven't perfected yet — first-time 3-stars give the most XP."
      }
    ]
  },
  {
    title: "Events & Seasons",
    icon: "🎪",
    faqs: [
      {
        q: "What are Limited-Time Events?",
        a: "Events are temporary game modifiers that appear as banners on your level map. Friday Night Frenzy doubles coins, Weekend Double XP boosts experience gains, and Midweek Madness offers special challenges. Admin-created custom events can include unique rewards!"
      },
      {
        q: "How often do events happen?",
        a: "Recurring events follow a weekly schedule — Midweek Madness on Wednesdays, Friday Night Frenzy on Fridays, and Weekend Double XP on Saturday-Sunday. Special events are announced in the News section on the homepage."
      },
      {
        q: "What are Seasonal items?",
        a: "During holiday seasons (Halloween, Winter, Spring, Summer), exclusive items become available. These are limited-edition collectibles — once the season ends, they can't be obtained until the event returns. They're highly prized in the Grand Exchange!"
      },
      {
        q: "Do events affect PvP?",
        a: "Some events do! During special PvP events, you might see boosted ranking rewards, unique tournament formats, or limited-time PvP arenas with special rules."
      },
      {
        q: "Where can I see upcoming events?",
        a: "Check the News section on the landing page and the event banner on your level map. Major events are also announced through in-game notifications when you log in."
      }
    ]
  },
  {
    title: "Technical & Account",
    icon: "⚙️",
    faqs: [
      {
        q: "Is my progress saved automatically?",
        a: "Yes! Your progress saves to our servers after every level completion. Log in on any device and pick up right where you left off."
      },
      {
        q: "The game is running slowly. What can I do?",
        a: "Try closing other browser tabs, clearing your cache, or refreshing the page. On mobile, make sure your browser is up to date. Reducing the number of animations in the game can also help on older devices."
      },
      {
        q: "How do I reset my password?",
        a: "Click 'Forgot Password' on the login page and enter your email. We'll send you a reset code to get back into your account."
      },
      {
        q: "Can I play on multiple devices?",
        a: "Yes! Just log in with the same account on any device. Your progress, inventory, and profile sync automatically through our servers."
      },
      {
        q: "How do I delete my account?",
        a: "Contact our Help Center to request account deletion. Please note this is permanent and cannot be undone — all progress, items, purchases, and profile data will be permanently removed."
      },
      {
        q: "I lost my progress. Can it be recovered?",
        a: "If you were logged in, your progress is safe on our servers — just log in again. If you were playing as a guest, unfortunately guest progress cannot be recovered. We strongly recommend creating an account!"
      },
      {
        q: "The game crashed during a level. Did I lose my life?",
        a: "If the crash happened before the level ended, your life should still be intact. The game only deducts a life when you officially fail a level. Just reopen the game and try the level again."
      },
      {
        q: "How do I report a bug?",
        a: "Visit our Help Center (accessible from the FAQ page or hamburger menu) and describe the issue. Include your player name, what level/World you were on, and what happened. Screenshots are super helpful!"
      }
    ]
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:bg-white/5 px-4 -mx-4 transition-colors"
        data-testid={`faq-item-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="text-white font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-amber-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-slate-300 text-sm leading-relaxed pl-4 border-l-2 border-amber-500/30 ml-1">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);

  // Filter FAQs by search query
  const filteredCategories = FAQ_CATEGORIES.map(cat => ({
    ...cat,
    faqs: cat.faqs.filter(faq =>
      !searchQuery ||
      faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.faqs.length > 0);

  const totalFAQs = FAQ_CATEGORIES.reduce((sum, cat) => sum + cat.faqs.length, 0);
  const filteredCount = filteredCategories.reduce((sum, cat) => sum + cat.faqs.length, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950/30 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-white/10 sticky top-0 z-50 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10 px-2"
            data-testid="faq-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🍓</span>
            <span className="text-lg font-bold text-white">Fruit Crush</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
            <HelpCircle className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Frequently Asked Questions</h1>
          <p className="text-slate-400">{totalFAQs} answers to help you crush it</p>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-lg mx-auto" data-testid="faq-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setActiveCategory(null); }}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30"
          />
          {searchQuery && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
              {filteredCount} result{filteredCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Category quick links */}
        <div className="flex flex-wrap gap-2 justify-center mb-8" data-testid="faq-category-tabs">
          <button
            onClick={() => { setActiveCategory(null); setSearchQuery(""); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeCategory === null && !searchQuery
                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                : 'bg-slate-800/40 text-slate-400 border border-transparent hover:bg-slate-800/60 hover:text-slate-300'
            }`}
          >
            All
          </button>
          {FAQ_CATEGORIES.map((cat, idx) => (
            <button
              key={idx}
              onClick={() => {
                setActiveCategory(activeCategory === idx ? null : idx);
                setSearchQuery("");
                if (activeCategory !== idx) {
                  setTimeout(() => {
                    document.getElementById(`faq-cat-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 50);
                }
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                activeCategory === idx
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  : 'bg-slate-800/40 text-slate-400 border border-transparent hover:bg-slate-800/60 hover:text-slate-300'
              }`}
            >
              <span>{cat.icon}</span>
              {cat.title}
            </button>
          ))}
        </div>

        {/* FAQ Categories */}
        <div className="space-y-6">
          {filteredCategories.map((category, idx) => {
            const originalIdx = FAQ_CATEGORIES.findIndex(c => c.title === category.title);
            if (activeCategory !== null && activeCategory !== originalIdx) return null;
            return (
              <Card key={category.title} id={`faq-cat-${originalIdx}`} className="bg-slate-800/30 border-white/5 p-5 scroll-mt-24" data-testid={`faq-category-${category.title.toLowerCase().replace(/ /g, '-')}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xl">{category.icon}</span>
                  <h2 className="text-lg font-bold text-white">{category.title}</h2>
                  <span className="ml-auto text-xs text-slate-500">{category.faqs.length} question{category.faqs.length !== 1 ? 's' : ''}</span>
                </div>
                <div>
                  {category.faqs.map((faq, i) => (
                    <FAQItem key={i} question={faq.q} answer={faq.a} />
                  ))}
                </div>
              </Card>
            );
          })}
        </div>

        {filteredCategories.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">No matching questions found</p>
            <p className="text-slate-500 text-sm mt-1">Try different keywords</p>
          </div>
        )}

        {/* Still need help? */}
        <Card className="bg-gradient-to-r from-amber-600/10 to-orange-600/10 border-amber-500/20 p-8 mt-10 text-center">
          <MessageCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-white mb-1.5">Still have questions?</h3>
          <p className="text-slate-400 text-sm mb-4">Our support team is ready to help!</p>
          <Button 
            onClick={() => navigate("/help")}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:brightness-110 text-white"
            data-testid="faq-contact-support"
          >
            Contact Support
          </Button>
        </Card>
      </div>
    </div>
  );
}
